import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PostComposer from "@/components/PostComposer";
import PostCard from "@/components/PostCard";
import MatchCard from "@/components/MatchCard";
import PremiumDialog from "@/components/PremiumDialog";
import PhoneVerifyDialog from "@/components/PhoneVerifyDialog";
import AnniversaryBanner from "@/components/AnniversaryBanner";
import { HUBS } from "@/lib/constants";
import { Sparkle, ArrowRight, Compass, DeviceMobile, ShieldCheck, CreditCard } from "@phosphor-icons/react";
import { toast } from "sonner";
import usePageMeta from "@/lib/usePageMeta";

export default function Dashboard() {
  usePageMeta({ title: "Timeline", description: "Your Wingwomen timeline, anonymous community posts, similar-journey matches, and the AI Health Navigator." });
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openPremium, setOpenPremium] = useState(false);
  const [openPhone, setOpenPhone] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);

  const openBillingPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/premium/portal", { origin_url: window.location.origin });
      if (!data?.url) throw new Error("no url");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open billing portal");
      setPortalBusy(false);
    }
  };

  const load = async () => {
    const [p, m] = await Promise.all([api.get("/posts"), api.get("/matches", { params: { limit: 4 } })]);
    setPosts(p.data.posts); setMatches(m.data.matches);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-8 space-y-8">
        <AnniversaryBanner />
        <div>
          <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-3"><span className="rule" />Your timeline</div>
          <h1 className="font-display text-4xl sm:text-5xl leading-tight">
            Welcome, <span className="italic text-coral-700">{user?.anon_name}</span>.
          </h1>
          <p className="mt-3 text-ink/70 max-w-xl">
            Share what's on your heart. The circle is listening.
          </p>
        </div>

        <PostComposer onPosted={load} />

        <div className="space-y-4 stagger" data-testid="timeline-list">
          {loading ? (
            <div className="text-muted-foreground">Loading…</div>
          ) : posts.length === 0 ? (
            <div className="p-8 bg-white border border-border rounded-2xl text-center text-muted-foreground">
              No posts yet. Be the first to share.
            </div>
          ) : posts.map(p => <PostCard key={p.id} post={p} onChanged={load} />)}
        </div>
      </div>

      <aside className="lg:col-span-4 space-y-8">
        {/* AI Navigator card */}
        <Link to="/app/navigator" data-testid="dash-nav-card"
              className="block bg-ink text-cream rounded-2xl p-6 shadow-lift group hover:bg-ink/90 transition-colors">
          <Compass size={28} weight="duotone" className="text-coral-700" />
          <div className="font-display text-2xl mt-3 leading-snug">AI Health Navigator</div>
          <p className="text-cream/70 text-sm mt-2 leading-relaxed">
            Ask anything, from appointment prep to what peers like you have tried.
          </p>
          <div className="mt-4 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-coral-700">
            Talk now <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
          </div>
        </Link>

        {/* Top matches */}
        <div>
          <div className="flex items-baseline justify-between mb-4">
            <div className="text-xs tracking-[0.25em] uppercase text-sage-600"><span className="rule" />Similar journeys</div>
            <Link to="/app/matches" data-testid="dash-see-all-matches" className="text-xs text-coral-700 hover:underline">See all</Link>
          </div>
          <div className="space-y-3">
            {matches.length === 0 && <div className="text-sm text-muted-foreground">Matches will appear as more women join.</div>}
            {matches.slice(0, 3).map(m => <MatchCard key={m.user_id} match={m} compact />)}
          </div>
        </div>

        {/* Phone verify card */}
        {user?.phone_verified ? (
          <div className="bg-sage-50 border border-sage-100 rounded-2xl p-4 flex items-center gap-3" data-testid="dash-phone-verified">
            <ShieldCheck size={20} weight="duotone" className="text-sage-700 shrink-0" />
            <div className="text-xs leading-relaxed">
              <div className="font-display text-base text-ink">SMS pings on</div>
              <div className="text-muted-foreground">We'll text you when a peer messages you.</div>
            </div>
          </div>
        ) : (
          <button onClick={() => setOpenPhone(true)} data-testid="dash-phone-card"
                  className="block w-full text-left bg-white border border-border rounded-2xl p-4 hover:border-coral transition-colors">
            <div className="flex items-center gap-3">
              <DeviceMobile size={20} weight="duotone" className="text-coral-700 shrink-0" />
              <div className="text-xs leading-relaxed">
                <div className="font-display text-base">Never miss a whisper</div>
                <div className="text-muted-foreground">Verify your phone for SMS pings on new messages.</div>
              </div>
            </div>
          </button>
        )}

        {/* Premium upsell */}
        {!user?.premium && (
          <button onClick={() => setOpenPremium(true)} data-testid="dash-premium-card"
                  className="block w-full text-left bg-coral-50 border border-coral-100 rounded-2xl p-5 hover:border-coral transition-colors">
            <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-coral-700">
              <Sparkle size={14} weight="duotone" /> Premium · 14 days free
            </div>
            <div className="font-display text-lg mt-2 leading-snug">Unlimited matches + journey analytics.</div>
            <div className="mt-2 inline-flex items-center gap-1 text-xs text-coral-700">
              Start free trial <ArrowRight size={12} />
            </div>
          </button>
        )}

        {/* Premium billing management (only when premium + has a Stripe customer on file) */}
        {user?.premium && user?.stripe_customer_id && (
          <button onClick={openBillingPortal} disabled={portalBusy} data-testid="dash-billing-portal"
                  className="block w-full text-left bg-white border border-border rounded-2xl p-4 hover:border-coral transition-colors disabled:opacity-60">
            <div className="flex items-center gap-3">
              <CreditCard size={20} weight="duotone" className="text-coral-700 shrink-0" />
              <div className="text-xs leading-relaxed">
                <div className="font-display text-base">{portalBusy ? "Opening Stripe…" : "Manage billing"}</div>
                <div className="text-muted-foreground">Update card, download invoices, or cancel anytime.</div>
              </div>
            </div>
          </button>
        )}

        {/* Hubs */}
        <div>
          <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-4"><span className="rule" />Condition hubs</div>
          <div className="grid grid-cols-2 gap-2">
            {HUBS.map(h => (
              <Link key={h.id} to={`/app/hubs/${encodeURIComponent(h.id)}`}
                    data-testid={`hub-link-${h.id}`}
                    className="p-3 bg-white border border-border rounded-xl hover:border-coral transition-colors">
                <Sparkle size={16} weight="duotone" className="text-sage" />
                <div className="font-display text-base mt-1.5">{h.id}</div>
              </Link>
            ))}
          </div>
        </div>
      </aside>
      <PremiumDialog open={openPremium} onClose={() => setOpenPremium(false)} />
      <PhoneVerifyDialog open={openPhone} onClose={() => setOpenPhone(false)} />
    </div>
  );
}
